import React, { useState, useEffect, useRef } from 'react';
import { View, ScrollView, RefreshControl, StyleSheet, TouchableOpacity, Text } from 'react-native';
import type { TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import dayjs from 'dayjs';
import { afterInteractions } from 'src/core/utils/afterInteractions';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import NavBar from 'src/presentation/components/organisms/NavBar';
import RemoteImage from 'src/presentation/components/molecules/RemoteImage';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Footer from 'src/presentation/components/organisms/Footer';
import { useFooterHeight } from 'src/presentation/hooks/useFooterHeight';
import Loader from 'src/presentation/components/atoms/Loader';
import Title from 'src/presentation/components/atoms/Title';
import Translations from 'src/core/utils/Translations';
import {
	ContentBlockButton,
	ContentBlockDivider,
	ContentBlockForm,
	ContentBlockGallery,
	ContentBlockImage,
	ContentBlockList,
	ContentBlockLocation,
	ContentBlockText,
	ContentBlockTitle,
	ContentBlockVideo,
} from 'src/presentation/components/organisms/contentblocks';
import { usePageFromRoute } from 'src/presentation/hooks/usePageFromRoute';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useFormStore } from 'src/domain/stores/useFormStore';
import { useMyTimeTableStore } from 'src/domain/stores/useMyTimeTableStore';
import type { CustomScreenParams, RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import { navigatePage, pushPage } from 'src/presentation/helpers/NavigationHelper';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import type { ApiContentBlock } from 'src/core/models/api/ApiContentBlock';
import type { ApiPage } from 'src/core/models/api/ApiPage';

// Room above a focused field for its label and a bit of the field before it.
const FIELD_SCROLL_OFFSET = 120;

// The content view measures a field apart from the scroll position; React Native has it, its types do not.
type ScrollViewWithContent = ScrollView & { getInnerViewRef(): ReturnType<ScrollView['getNativeScrollRef']> };

function CustomScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenCustom>>();
	const eventStore = useEventStore();
	const footerHeight = useFooterHeight();
	const [interactionsComplete, setInteractionsComplete] = useState(false);
	const [refreshing, setRefreshing] = useState(false);
	const scrollRef = useRef<ScrollViewWithContent>(null);
	const focusedFieldRef = useRef<TextInput | null>(null);

	// The window does not shrink for the keyboard, so a form field low on the page would sit behind it. The form's
	// keyboard spacer grows the content once the keyboard is up, which is why this runs again on a size change.
	const scrollToFocusedField = () => {
		const field = focusedFieldRef.current;
		const content = scrollRef.current?.getInnerViewRef();
		if (!field?.isFocused() || !content) return;
		field.measureLayout(
			content,
			(_x, y) => {
				// The next key may have moved focus on while this was measured.
				if (focusedFieldRef.current !== field || !field.isFocused()) return;
				scrollRef.current?.scrollTo({ y: Math.max(0, y - FIELD_SCROLL_OFFSET) });
			},
			() => undefined,
		);
	};

	// Returning from a form re-enters this screen, and then the form store holds the page it was
	// submitted from. Which of the two wins is decided once, on mount, exactly as before: the route
	// only overrules the form store when the two point at different pages. What happens after that --
	// preferring the fresher store copy, and fetching the page when a URL is all we have -- is the
	// hook's job.
	const [pageParams] = useState<CustomScreenParams | undefined>(() => {
		const formParams = useFormStore.getState().formParams as { data?: ApiPage } | null;
		if (!formParams) {
			return route?.params;
		}

		const handedOver = route?.params?.data;
		const data = formParams.data && handedOver && formParams.data.id !== handedOver.id ? handedOver : formParams.data;

		return { ...route?.params, data };
	});

	const { page: data, isLoading } = usePageFromRoute(pageParams);

	// A session title is a full sentence, so the nav bar could only ever show a fragment of it: the page
	// prints the title above its text and the bar stays empty. Also when the beheerder opened the page with
	// a title block of their own, because that names the speaker, not the session.
	const showPageTitle = data?.type === 'ProgramItem';
	const title = showPageTitle ? '' : (data?.name ?? '');

	// The list and the block schedule both let a participant like a session; the detail page it opens
	// carries the same heart, in the space the title left behind. A page we do not know (a block opened
	// from a stale copy of the day) has no id to favorite, so it gets no heart either.
	const isSession = data?.type === 'ProgramItem' && data.id > 0;
	const favorite = useMyTimeTableStore((state) => state.myTimeTableData.some((p) => p.pageid === data?.id));
	const toggleFavorite = useMyTimeTableStore((state) => state.toggleFavorite);
	const favoritesDisabled = useMyTimeTableStore((state) => state.loadFailed);

	// When and where the session runs. The list and the blocks both say it, so the page it opens does too --
	// and a session the beheerder left without content blocks still tells the participant something.
	const sessionFacts = (() => {
		if (data?.type !== 'ProgramItem') return '';

		const { start_time: start, end_time: end, stage } = data.data;
		const time =
			start && end
				? `${dayjs.unix(start).format(Constants.timeFormat)} - ${dayjs.unix(end).format(Constants.timeFormat)}`
				: '';

		return [time, stage].filter(Boolean).join(' | ');
	})();

	useEffect(() => {
		const task = afterInteractions(() => {
			setInteractionsComplete(true);
		});

		return () => task.cancel();
	}, []);

	const handleRefresh = () => {
		setRefreshing(true);
		eventStore
			.fetchEvent(eventStore.activeEventUuid, true)
			.then(() => {
				setRefreshing(false);
			})
			.catch(() => {
				setRefreshing(false);
			});
	};

	// A menu item, a location or a news item is opened from a list that already shows its photo, and the detail
	// screen dropped it: what was a photo plus a story read as a bare paragraph (#404).
	const headerImage = (): string | null => {
		if (!data || !['FoodItem', 'LocationItem', 'NewsItem'].includes(data.type)) return null;

		const image = (data.data as { page_image?: string } | undefined)?.page_image;

		return image && image.length > 0 ? image : null;
	};

	const onButtonPress = (nav: NativeStackNavigationProp<RootStackParamList>, page: string, pageData: ApiPage) => {
		pushPage(nav, page, pageData);
	};

	const renderContentBlockBasedOnType = (
		nav: NativeStackNavigationProp<RootStackParamList>,
		data: ApiContentBlock,
		key: number,
		length: number,
		formComponents: string[],
	) => {
		switch (data.type) {
			case 'ButtonBlock':
				return <ContentBlockButton data={data} first={key === 0} key={key} last={length === key} />;
			case 'VideoBlock':
				return <ContentBlockVideo data={data} key={key} />;
			case 'ImageBlock':
				return <ContentBlockImage data={data} first={key === 0} key={key} last={length === key} />;
			case 'GalleryBlock':
				return (
					<ContentBlockGallery
						data={data}
						key={key}
						onClick={(screen, d) => {
							navigatePage(navigation, screen, d.data);
						}}
					/>
				);
			case 'ListBlock':
				return (
					<ContentBlockList data={data} key={key} onButtonPress={(page, pageData) => onButtonPress(nav, page, pageData)} />
				);
			case 'LocationBlock':
				return <ContentBlockLocation data={data} first={key === 0} key={key} last={length === key} />;
			case 'FormBlock':
				return (
					<ContentBlockForm
						data={data}
						first={key === 0}
						firstOfType={formComponents.indexOf('FormBlock') !== -1}
						key={key}
						last={length === key}
						route={route}
						onFieldFocus={(field) => {
							focusedFieldRef.current = field;
							scrollToFocusedField();
						}}
					/>
				);
			case 'TitleBlock':
				return <ContentBlockTitle data={data} key={key} />;
			case 'TextBlock':
				return <ContentBlockText data={data} key={key} />;
			case 'DividerBlock':
				return <ContentBlockDivider key={key} />;
			default:
				return null;
		}
	};

	const renderContentBlocks = (
		nav: NativeStackNavigationProp<RootStackParamList>,
		blocks: ApiPage['contentblocks'] | null,
	) => {
		if (!blocks) return;
		const arrayLength = blocks.length - 1;
		const formComponents: string[] = [];
		return blocks.map((contentblock, index) => {
			if (contentblock.type === 'FormBlock') {
				formComponents.push(contentblock.type);
			}
			return renderContentBlockBasedOnType(nav, contentblock, index, arrayLength, formComponents);
		});
	};

	return (
		<View style={{ flex: 1 }}>
			<NavBar
				backBtn={true}
				bgImage={eventStore.activeTheme.header_bg_image}
				bgcolor={eventStore.activeTheme.header_bg_color}
				color={eventStore.activeTheme.header_text_color}
				editBtn={false}
				rightAction={
					isSession && data ? (
						<TouchableOpacity
							accessibilityLabel={Translations.buttonFavorite}
							accessibilityRole="button"
							accessibilityState={{ selected: favorite }}
							hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
							style={favoritesDisabled ? styles.favoriteDisabled : null}
							onPress={() => {
								toggleFavorite(data.id, data.event_uuid).catch(() => undefined);
							}}
						>
							<Ionicons color={Colors.red} name={favorite ? 'heart' : 'heart-outline'} size={26} />
						</TouchableOpacity>
					) : undefined
				}
				statusBarStyle={eventStore.activeTheme.statusbar_color}
				title={title}
			/>
			{!interactionsComplete || isLoading ? (
				<Loader />
			) : (
				<ScrollView
					contentContainerStyle={{
						backgroundColor: Colors.white,
						flexDirection: 'column',
						flexGrow: 1,
						paddingBottom: footerHeight,
					}}
					keyboardShouldPersistTaps="handled"
					ref={scrollRef}
					refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
					style={{ flex: 1 }}
					onContentSizeChange={scrollToFocusedField}
				>
					{headerImage() ? (
						<View testID="page-header-image">
							<RemoteImage style={styles.headerImage} uri={headerImage() ?? ''} />
						</View>
					) : null}
					{showPageTitle ? <Title style={styles.pageTitle} text={data?.name ?? ''} /> : null}
					{sessionFacts ? <Text style={styles.sessionFacts}>{sessionFacts}</Text> : null}
					{data?.contentblocks && data.contentblocks.length > 0 ? (
						<>{renderContentBlocks(navigation, data.contentblocks)}</>
					) : headerImage() ? null : (
						<EmptyState
							icon="document-text-outline"
							message={Translations.textNoContentMessage}
							title={Translations.textNoContentTitle}
						/>
					)}
				</ScrollView>
			)}
			<Footer
				backgroundColor={eventStore.activeTheme.footer_bg_color}
				backgroundImage={eventStore.activeTheme.footer_bg_image}
				backgroundType={eventStore.activeTheme.footer_bg_type}
			/>
		</View>
	);
}

export default CustomScreen;

const styles = StyleSheet.create({
	headerImage: {
		height: 210,
		width: '100%',
	},
	pageTitle: {
		// The first text block brings its own air, so the title sits closer to it than to the nav bar.
		marginBottom: 0,
	},
	favoriteDisabled: {
		// The server favorites could not be loaded, so the heart's state is unknown.
		opacity: 0.35,
	},
	sessionFacts: {
		fontSize: Dimens.fontSize17,
		fontFamily: Fonts.montserratRegular,
		color: Colors.greyTextDark,
		paddingHorizontal: Dimens.padding20,
		paddingBottom: Dimens.padding8,
	},
});
